import {usePlantStartOfToday} from '@hconnect/upm'
import moment from 'moment-timezone'
import {useMemo} from 'react'

import type {MomentRange} from '../types'

const LONG_TERM_PLAN_START_DATE = '2024-01-01'
const LONG_TERM_PLAN_MAX_FULL_YEARS = 2

const getLongTermPlanBoundary = (startOfToday: moment.Moment): MomentRange => {
  /** The boundary start is clamped to the earlier of the fixed LONG_TERM_PLAN_START_DATE
   * and the start of the current (plant-clock) year. This keeps the boundary valid for
   * both the real app and mocked tests without depending on the (now removed) mock store:
   * - Production: the clock is always in the future (>= 2024), so `min` always returns
   *   LONG_TERM_PLAN_START_DATE (2024-01-01) — identical to the previous hardcoded value.
   * - Cypress (clock mocked to e.g. 2019): `min` returns the mocked year start (2019-01-01),
   *   reproducing the old `mockStore.scenario().start.startOf('year')` behavior.
   * Deriving the clamp from `startOfToday` (instead of a mock flag) prevents the boundary
   * from inverting (start > end) when "now" is before LONG_TERM_PLAN_START_DATE.
   * */
  const start = moment.min(
    moment.utc(LONG_TERM_PLAN_START_DATE),
    startOfToday.clone().startOf('year')
  )
  const end = startOfToday.clone().add(LONG_TERM_PLAN_MAX_FULL_YEARS, 'year').startOf('year')
  return [start, end]
}

export const useLongTermPlanBoundary = (): MomentRange => {
  const startOfToday = usePlantStartOfToday()

  return useMemo(() => getLongTermPlanBoundary(startOfToday), [startOfToday])
}
